const GROUP_BURST_MS = 10000;

const METHOD_META = {
  ach: {
    label: 'Transferencia ACH',
    plural: 'Transferencias ACH',
    aliases: ['ach', 'transferencia_ach', 'transferencia ach'],
  },
  transfer: {
    label: 'Transferencia',
    plural: 'Transferencias',
    aliases: ['transfer', 'transferencia', 'bank_transfer', 'wire', 'sync'],
  },
  check: {
    label: 'Cheque',
    plural: 'Cheques',
    aliases: ['check', 'cheque'],
  },
  cash: {
    label: 'Efectivo',
    plural: 'Efectivo',
    aliases: ['cash', 'efectivo'],
  },
  card: {
    label: 'Tarjeta',
    plural: 'Tarjetas',
    aliases: ['card', 'tarjeta', 'credit_card', 'debit_card'],
  },
  other: {
    label: 'Otro',
    plural: 'Otros',
    aliases: ['other', 'otro', 'abasto'],
  },
};

export const PAYMENT_METHOD_META = METHOD_META;

export const formatCurrency = (value) => {
  const num = toNumber(value);
  return new Intl.NumberFormat('es-GT', {
    style: 'currency',
    currency: 'GTQ',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

export const formatDate = (value) => {
  if (!value) return '--';

  try {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-').map(Number);
      return new Date(year, month - 1, day).toLocaleDateString('es-GT', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('es-GT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return String(value);
  }
};

export const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

export const normalizeMethodType = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return 'other';

  const normalized = raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_');

  const found = Object.entries(METHOD_META).find(([, meta]) =>
    meta.aliases.some((alias) => normalized.includes(alias))
  );

  return found ? found[0] : 'other';
};

export const getMethodLabel = (value) => {
  const type = normalizeMethodType(value);
  const raw = String(value || '').trim();
  if (type === 'other' && raw && !METHOD_META.other.aliases.includes(raw.toLowerCase())) {
    return capitalize(raw);
  }
  if (type === 'other' && raw.toLowerCase() === 'abasto') return 'Abasto';
  return METHOD_META[type]?.label || METHOD_META.other.label;
};

export const getMethodSummaryLabel = (summaryItem) => {
  const type = summaryItem?.type || 'other';
  const meta = METHOD_META[type] || METHOD_META.other;
  const label = summaryItem?.label || meta.label;
  const count = Number(summaryItem?.count || 0);
  return count > 1 ? `${label} x${count}` : label;
};

export const isCashMethod = (methodOrType) => {
  const type = typeof methodOrType === 'string' ? normalizeMethodType(methodOrType) : methodOrType?.type;
  return type === 'cash';
};

export const methodRequiresAttachment = (methodOrType) => {
  const type = typeof methodOrType === 'string' ? normalizeMethodType(methodOrType) : methodOrType?.type;
  return ['ach', 'check', 'card'].includes(type);
};

export const normalizePaymentsList = (payload = []) => {
  const source = Array.isArray(payload) ? payload : (payload?.results || []);
  const alreadyConsolidated = [];
  const technicalRows = [];

  source.forEach((row) => {
    if (Array.isArray(row?.methods)) {
      alreadyConsolidated.push(normalizeConsolidatedPayment(row));
    } else {
      technicalRows.push(row);
    }
  });

  const parentGroups = new Map();
  const fallbackRows = [];

  technicalRows.forEach((row) => {
    const parentKey = resolveParentGroupKey(row);
    if (parentKey) {
      if (!parentGroups.has(parentKey)) parentGroups.set(parentKey, []);
      parentGroups.get(parentKey).push(row);
      return;
    }
    fallbackRows.push(row);
  });

  const fallbackGroups = buildFallbackGroups(fallbackRows);
  const consolidated = [
    ...alreadyConsolidated,
    ...Array.from(parentGroups.entries()).map(([key, rows]) => buildConsolidatedPayment(rows, `parent:${key}`)),
    ...fallbackGroups.map((group) => buildConsolidatedPayment(group.rows, group.key)),
  ];

  return consolidated.sort((a, b) => {
    const left = b.sortTime || 0;
    const right = a.sortTime || 0;
    if (left !== right) return left - right;
    return String(b.code || '').localeCompare(String(a.code || ''));
  });
};

export const normalizePaymentDetailData = (detailsInput = [], seed = {}) => {
  const details = Array.isArray(detailsInput) ? detailsInput.filter(Boolean) : [detailsInput].filter(Boolean);

  if (details.length === 0 && seed?.methods) {
    return normalizeConsolidatedPayment(seed);
  }

  const methods = uniqueBy(
    details.map((detail) => detailToMethod(detail)),
    getMethodIdentityKey
  );
  const totalAmount = sumMethods(methods);
  const primary = details[0] || {};
  const receipts = uniqueBy(
    details.flatMap((detail) => normalizeReceipts(detail)),
    (receipt) => receipt.id || `${receipt.number}-${receipt.date}-${receipt.amount}`
  );
  const relatedDocuments = uniqueBy(
    details.flatMap((detail) => normalizeAllocationDocuments(detail?.allocations || [])),
    (doc) => doc.id || doc.code
  );
  const adjustments = uniqueBy(
    details.flatMap((detail) => normalizeList(detail?.adjustments)),
    (item) => item.id || `${item.invoice_id}-${item.type}-${item.amount}-${item.created_at}`
  );
  const credits = uniqueBy(
    details.flatMap((detail) => normalizeList(detail?.credits)),
    (item) => item.id || `${item.credit_note_id}-${item.invoice_id}-${item.amount_applied}`
  );
  const corrections = uniqueBy(
    details.flatMap((detail) => normalizeList(detail?.corrections)),
    (item) => item.id || `${item.corrected_at}-${item.reason}`
  );
  const notes = uniqueText([
    seed?.notes,
    seed?.comment,
    ...details.map((detail) => detail?.comment),
  ]);

  const paymentIds = uniqueBy(
    details.map((detail) => detail?.id).filter(Boolean),
    (value) => String(value)
  );
  const createdAt = firstValue(seed?.createdAt, seed?.created_at, primary?.createdAt, primary?.created_at);
  const provider = normalizeProvider(seed?.provider || primary?.supplier);
  const methodSummary = summarizeMethods(methods);
  const receiptStatus = resolveReceiptStatus(receipts, seed?.totalAmount || totalAmount);
  const status = resolveStatus(details);

  return {
    id: seed?.id || `detail:${paymentIds.join('-') || primary?.id || 'payment'}`,
    code: seed?.code || makePaymentCode(paymentIds[0] || primary?.id),
    provider,
    paymentDate: firstValue(seed?.paymentDate, seed?.date, primary?.date, primary?.paymentDate),
    dateEstimated: details.some((detail) => Boolean(detail?.date_estimated)) || Boolean(seed?.dateEstimated || seed?.date_estimated),
    createdAt,
    createdBy: firstValue(seed?.createdBy, seed?.created_by, primary?.createdBy, primary?.created_by),
    totalAmount: seed?.totalAmount || totalAmount,
    appliedTotal: seed?.appliedTotal || details.reduce((sum, detail) => sum + sumAllocations(detail?.allocations), 0),
    status,
    relatedDocuments,
    relatedDocument: relatedDocuments[0] || null,
    receipt: {
      status: receiptStatus,
      items: receipts,
      hasReceipt: receipts.length > 0 || details.some((detail) => detail?.has_receipt),
    },
    notes,
    adjustments,
    credits,
    corrections,
    methods,
    attachmentsCount: methods.reduce((sum, method) => sum + toNumber(method.attachmentsCount), 0),
    methodSummary,
    paymentIds,
    raw: details,
    seed,
  };
};

const normalizeConsolidatedPayment = (payment) => {
  const methods = normalizeList(payment.methods).map((method) => normalizeMethod(method));
  const relatedDocuments = normalizeRelatedDocuments(payment);
  const receipts = normalizeReceipts(payment);
  const methodSummary = summarizeMethods(methods);
  const totalAmount = firstNumber(payment.totalAmount, payment.total_amount, payment.amount, sumMethods(methods));
  const paymentIds = normalizeList(payment.paymentIds || payment.payment_ids).filter(Boolean);

  return {
    id: payment.id || payment.groupKey || payment.group_key || `consolidated:${payment.code || paymentIds.join('-')}`,
    code: payment.code || makePaymentCode(payment.id || paymentIds[0]),
    provider: normalizeProvider(payment.provider || payment.supplier),
    paymentDate: payment.paymentDate || payment.payment_date || payment.date,
    dateEstimated: Boolean(payment.dateEstimated || payment.date_estimated),
    createdAt: payment.createdAt || payment.created_at,
    createdBy: payment.createdBy || payment.created_by,
    totalAmount,
    appliedTotal: firstNumber(payment.appliedTotal, payment.applied_total, totalAmount),
    status: normalizeStatus(payment.status || payment.is_active),
    relatedDocuments,
    relatedDocument: payment.relatedDocument || payment.related_document || relatedDocuments[0] || null,
    receipt: payment.receipt || {
      status: resolveReceiptStatus(receipts, totalAmount),
      items: receipts,
      hasReceipt: receipts.length > 0 || Boolean(payment.has_receipt),
    },
    notes: normalizeList(payment.notes),
    adjustments: normalizeList(payment.adjustments),
    credits: normalizeList(payment.credits),
    corrections: normalizeList(payment.corrections),
    methods,
    attachmentsCount: firstNumber(payment.attachmentsCount, payment.attachments_count, sumAttachments(methods)),
    methodSummary,
    paymentIds,
    groupKey: payment.groupKey || payment.group_key || payment.id,
    sortTime: parseTime(payment.createdAt || payment.created_at || payment.paymentDate || payment.date),
    raw: payment.raw || payment,
  };
};

const buildFallbackGroups = (rows) => {
  const groupsBySignature = new Map();

  [...rows]
    .sort((a, b) => {
      const left = parseTime(a.created_at || a.createdAt || a.dateTime || a.date);
      const right = parseTime(b.created_at || b.createdAt || b.dateTime || b.date);
      if (left !== right) return left - right;
      return toNumber(a.id) - toNumber(b.id);
    })
    .forEach((row) => {
      const signature = fallbackSignature(row);
      const createdMs = parseTime(row.created_at || row.createdAt || row.dateTime || row.date);
      const existing = groupsBySignature.get(signature) || [];
      const lastGroup = existing[existing.length - 1];

      // The current backend has no real parent operation id. This fallback only
      // groups method rows that share supplier, creator, related docs and a short
      // creation burst. Replace this with the backend group id when available.
      if (
        lastGroup &&
        (!createdMs || !lastGroup.lastMs || Math.abs(createdMs - lastGroup.lastMs) <= GROUP_BURST_MS)
      ) {
        lastGroup.rows.push(row);
        lastGroup.lastMs = createdMs || lastGroup.lastMs;
      } else {
        existing.push({
          key: `fallback:${signature}:${existing.length + 1}`,
          rows: [row],
          lastMs: createdMs,
        });
        groupsBySignature.set(signature, existing);
      }
    });

  return Array.from(groupsBySignature.values()).flat();
};

const buildConsolidatedPayment = (rows, groupKey) => {
  const cleanRows = normalizeList(rows);
  const sortedRows = [...cleanRows].sort((a, b) => {
    const left = parseTime(a.created_at || a.createdAt || a.dateTime || a.date);
    const right = parseTime(b.created_at || b.createdAt || b.dateTime || b.date);
    if (left !== right) return left - right;
    return toNumber(a.id) - toNumber(b.id);
  });
  const primary = sortedRows[0] || {};
  const methods = sortedRows.map((row) => normalizeMethod(row));
  const relatedDocuments = uniqueBy(
    sortedRows.flatMap((row) => normalizeRelatedDocuments(row)),
    (doc) => doc.id || doc.code
  );
  const receipts = uniqueBy(
    sortedRows.flatMap((row) => normalizeReceipts(row)),
    (receipt) => receipt.id || `${receipt.number}-${receipt.date}-${receipt.amount}`
  );
  const paymentIds = sortedRows.map((row) => row.id || row.payment_id).filter(Boolean);
  const totalAmount = sumMethods(methods);
  const methodSummary = summarizeMethods(methods);
  const sortTime = Math.max(
    ...sortedRows.map((row) => parseTime(row.created_at || row.createdAt || row.dateTime || row.date)).filter(Boolean),
    0
  );

  return {
    id: groupKey,
    code: primary.code || primary.payment_code || makePaymentCode(paymentIds[0] || primary.id),
    provider: normalizeProvider(primary.provider || primary.supplier),
    paymentDate: primary.paymentDate || primary.payment_date || primary.date || primary.dateTime,
    dateEstimated: sortedRows.some((row) => Boolean(row.dateEstimated || row.date_estimated)),
    createdAt: primary.created_at || primary.createdAt || primary.dateTime,
    createdBy: primary.created_by || primary.createdBy || primary.created_by_id || primary.createdById,
    totalAmount,
    appliedTotal: sortedRows.reduce((sum, row) => sum + toNumber(row.applied_total || row.appliedTotal), 0),
    status: resolveStatus(sortedRows),
    relatedDocuments,
    relatedDocument: relatedDocuments[0] || null,
    receipt: {
      status: resolveReceiptStatus(receipts, totalAmount),
      items: receipts,
      hasReceipt: receipts.length > 0 || sortedRows.some((row) => row.has_receipt),
    },
    notes: uniqueText(sortedRows.flatMap((row) => [row.notes, row.comment])),
    adjustments: uniqueBy(sortedRows.flatMap((row) => normalizeList(row.adjustments)), (item) => item.id || JSON.stringify(item)),
    credits: uniqueBy(sortedRows.flatMap((row) => normalizeList(row.credits)), (item) => item.id || JSON.stringify(item)),
    methods,
    attachmentsCount: sumAttachments(methods),
    methodSummary,
    paymentIds,
    groupKey,
    fallbackGroup: groupKey.startsWith('fallback:'),
    sortTime,
    raw: sortedRows,
  };
};

const detailToMethod = (detail) => {
  const doc = detail?.document || null;
  const bank = detail?.bank_account || doc?.bank_account || null;
  const attachments = normalizeDocumentAttachments(doc);

  return normalizeMethod({
    id: detail?.id,
    method: detail?.method || detail?.paymentMethod,
    amount: detail?.amount,
    reference: doc?.number || detail?.reference,
    bank_account: bank,
    date: detail?.date || doc?.createdAt,
    document: doc,
    attachments,
    attachmentsCount: attachments.length,
    is_active: detail?.is_active,
    raw: detail,
  });
};

const normalizeMethod = (row) => {
  const rawType = firstValue(row?.method, row?.paymentMethod, row?.type, row?.payment_method);
  const type = normalizeMethodType(rawType);
  const attachments = row?.attachments ? normalizeAttachments(row.attachments) : normalizeDocumentAttachments(row?.document || row);
  const documentAttachmentCount = firstNumber(
    row?.attachmentsCount,
    row?.attachments_count,
    row?.document_attachments_count,
    attachments.length
  );
  const hasPrimaryFile = Boolean(
    firstValue(row?.document_file, row?.paymentFile, row?.payment_file, row?.document?.paymentFile)
  );
  const attachmentsCount = Math.max(attachments.length, documentAttachmentCount + (hasPrimaryFile && attachments.length === 0 ? 1 : 0));

  return {
    id: row?.id || row?.payment_id || row?.method_id,
    type,
    rawType,
    label: getMethodLabel(rawType),
    amount: toNumber(firstValue(row?.amount, row?.total, row?.totalAmount, row?.total_amount)),
    reference: firstValue(row?.reference, row?.document_number, row?.document?.number, row?.number),
    bank: normalizeBank(firstValue(row?.bank, row?.bank_account, row?.bankAccount, row?.document?.bank_account)),
    date: firstValue(row?.paymentDate, row?.payment_date, row?.date, row?.dateTime, row?.document?.createdAt),
    attachments,
    attachmentsCount,
    receiptState: resolveMethodReceiptState(type, attachmentsCount),
    isActive: row?.is_active !== false,
    raw: row?.raw || row,
  };
};

const normalizeDocumentAttachments = (document) => {
  if (!document) return [];

  const primaryFile = firstValue(document.paymentFile, document.payment_file, document.file, document.document_file);
  const primary = primaryFile
    ? [{
        id: document.id ? `${document.id}:primary` : primaryFile,
        url: primaryFile,
        name: getFileName(primaryFile),
        kind: getFileKind(primaryFile),
        primary: true,
      }]
    : [];

  return uniqueBy([
    ...primary,
    ...normalizeAttachments(document.attachments),
  ], (attachment) => attachment.url || attachment.id || attachment.name);
};

const normalizeAttachments = (attachments) => normalizeList(attachments).map((attachment, index) => {
  if (typeof attachment === 'string') {
    return {
      id: attachment,
      url: attachment,
      name: getFileName(attachment),
      kind: getFileKind(attachment),
    };
  }

  const url = firstValue(attachment?.url, attachment?.file_url, attachment?.file, attachment?.paymentFile);
  return {
    id: attachment?.id || url || index,
    url,
    name: firstValue(attachment?.name, attachment?.filename, getFileName(url), `Archivo ${index + 1}`),
    size: attachment?.size,
    kind: firstValue(attachment?.kind, attachment?.type, getFileKind(url)),
    raw: attachment,
  };
}).filter((attachment) => attachment.url || attachment.name);

const normalizeRelatedDocuments = (row) => {
  const candidates = [
    row?.relatedDocument,
    row?.related_document,
    row?.documento,
    row?.invoice,
    row?.invoices,
    row?.related_documents,
    row?.documents,
  ];

  return uniqueBy(
    candidates.flatMap((item) => normalizeDocumentList(item)),
    (doc) => doc.id || doc.code
  );
};

const normalizeAllocationDocuments = (allocations) => normalizeList(allocations).map((allocation) => {
  const number = firstValue(allocation?.invoice__number, allocation?.invoice_number, allocation?.number);
  const series = firstValue(allocation?.invoice__series, allocation?.invoice_series, allocation?.series);
  return {
    id: firstValue(allocation?.invoice_id, allocation?.id),
    code: [number, series].filter(Boolean).join(' - ') || String(firstValue(allocation?.invoice_id, allocation?.id, 'Documento')),
    number,
    series,
    date: firstValue(allocation?.invoice__date, allocation?.invoice_date, allocation?.date),
    amount: firstValue(allocation?.amount, allocation?.amount_applied),
    balance: allocation?.invoice_balance,
    supplier: firstValue(allocation?.invoice__supplier__companyName, allocation?.invoice__supplier__legalName, allocation?.supplier),
    raw: allocation,
  };
}).filter((doc) => doc.id || doc.code);

const normalizeDocumentList = (item) => {
  if (!item) return [];
  if (Array.isArray(item)) return item.flatMap((value) => normalizeDocumentList(value));

  if (typeof item === 'string' || typeof item === 'number') {
    return [{ id: String(item), code: String(item) }];
  }

  return [{
    id: firstValue(item.id, item.invoice_id, item.document_id),
    code: String(firstValue(item.code, item.number, item.invoice_number, item.name, item.id, 'Documento')),
    number: firstValue(item.number, item.invoice_number),
    series: item.series,
    total: firstValue(item.total, item.amount),
    date: item.date,
    raw: item,
  }];
};

const normalizeReceipts = (row) => {
  const candidates = [
    row?.receipt,
    row?.receipts,
    row?.receipt_details,
    row?.provider_receipt,
  ];

  return candidates.flatMap((item) => normalizeReceiptList(item)).filter((receipt) => receipt.number || receipt.id || receipt.file);
};

const normalizeReceiptList = (item) => {
  if (!item) return [];
  if (Array.isArray(item)) return item.flatMap((value) => normalizeReceiptList(value));

  if (typeof item === 'string' || typeof item === 'number') {
    return [{ id: String(item), number: String(item), label: String(item) }];
  }

  const file = firstValue(item.file, item.file_url, item.receiptFile, item.receipt_file);
  return [{
    id: item.id,
    number: firstValue(item.number, item.code),
    label: firstValue(item.number, item.code, item.id),
    date: item.date,
    amount: toNumber(item.amount),
    receiptType: firstValue(item.receipt_type, item.rType, item.type),
    isPhysical: firstValue(item.is_physical, item.is_ph),
    file,
    createdAt: firstValue(item.createdAt, item.created_at),
    uploadedBy: firstValue(item.uploaded_by, item.created_by),
    raw: item,
  }];
};

const summarizeMethods = (methods) => {
  const map = new Map();

  normalizeList(methods).forEach((method) => {
    const type = method?.type || normalizeMethodType(method?.rawType || method?.method);
    const key = `${type}:${method?.label || METHOD_META[type]?.label || METHOD_META.other.label}`;
    const current = map.get(key) || {
      type,
      label: method?.label || METHOD_META[type]?.label || METHOD_META.other.label,
      count: 0,
      total: 0,
    };
    current.count += 1;
    current.total += toNumber(method?.amount);
    map.set(key, current);
  });

  return Array.from(map.values());
};

const resolveParentGroupKey = (row) => {
  const keys = [
    'payment_group_id',
    'paymentGroupId',
    'transaction_group_id',
    'transactionGroupId',
    'purchase_payment_id',
    'purchasePaymentId',
    'payment_parent_id',
    'paymentParentId',
    'parent_payment_id',
    'parentPaymentId',
    'receipt_payment_id',
    'receiptPaymentId',
    'group_id',
    'groupId',
  ];

  for (const key of keys) {
    if (row?.[key]) return `${key}:${row[key]}`;
  }

  if (row?.payment_id && row?.id && row.payment_id !== row.id) {
    return `payment_id:${row.payment_id}`;
  }

  return null;
};

const fallbackSignature = (row) => {
  const provider = normalizeProvider(row?.provider || row?.supplier);
  const docs = normalizeRelatedDocuments(row).map((doc) => doc.id || doc.code).sort().join(',');
  const createdBy = firstValue(row?.created_by_id, row?.createdById, row?.created_by, row?.createdBy, 'unknown');
  const date = String(firstValue(row?.paymentDate, row?.payment_date, row?.date, '')).slice(0, 10);
  return [
    provider.id || provider.name || 'provider',
    date,
    createdBy,
    docs || 'no-docs',
  ].join('|');
};

const normalizeProvider = (value) => {
  if (!value) return { id: null, name: 'Sin proveedor' };
  if (typeof value === 'string') return { id: null, name: value || 'Sin proveedor' };
  return {
    id: value.id || value.supplier_id || null,
    name: firstValue(value.name, value.companyName, value.legalName, value.label, value.supplier, 'Sin proveedor'),
    raw: value,
  };
};

const normalizeBank = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return { label: value };
  const bankName = firstValue(value.bankName, value.bank, value.name);
  const account = firstValue(value.accNumber, value.accountNumber, value.number);
  const type = firstValue(value.accType, value.type);
  return {
    id: value.id || null,
    label: [bankName, account].filter(Boolean).join(' - ') || null,
    bankName,
    account,
    type,
    raw: value,
  };
};

const resolveMethodReceiptState = (type, attachmentsCount) => {
  if (attachmentsCount > 0) return 'attached';
  if (type === 'cash') return 'not_applicable';
  if (methodRequiresAttachment(type)) return 'pending';
  return 'none';
};

const resolveReceiptStatus = (receipts, totalAmount) => {
  if (!receipts.length) return 'pending';
  const receiptTotal = receipts.reduce((sum, receipt) => sum + toNumber(receipt.amount), 0);
  if (receiptTotal > 0 && Math.abs(receiptTotal - toNumber(totalAmount)) > 0.01) return 'incomplete';
  return 'attached';
};

const resolveStatus = (items) => {
  const list = normalizeList(items);
  if (list.length === 0) return { value: 'active', label: 'Activo' };
  const activeCount = list.filter((item) => item?.is_active !== false && item?.isActive !== false).length;
  if (activeCount === 0) return { value: 'cancelled', label: 'Anulado' };
  if (activeCount < list.length) return { value: 'partial', label: 'Mixto' };
  return { value: 'active', label: 'Activo' };
};

const normalizeStatus = (value) => {
  if (typeof value === 'object' && value?.value) return value;
  if (value === false || String(value || '').toLowerCase() === 'cancelled') {
    return { value: 'cancelled', label: 'Anulado' };
  }
  if (String(value || '').toLowerCase() === 'partial') {
    return { value: 'partial', label: 'Mixto' };
  }
  return { value: 'active', label: 'Activo' };
};

const getFileKind = (url = '') => {
  const clean = String(url || '').split('?')[0].toLowerCase();
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/.test(clean)) return 'image';
  if (/\.pdf$/.test(clean)) return 'pdf';
  if (/\.(docx?|xlsx?|pptx?)$/.test(clean)) return 'document';
  return 'unknown';
};

const getFileName = (url = '') => {
  if (!url) return 'Archivo';
  try {
    const clean = String(url).split('?')[0].split('#')[0];
    return decodeURIComponent(clean.split('/').pop() || 'Archivo');
  } catch {
    return String(url).split('/').pop() || 'Archivo';
  }
};

const makePaymentCode = (id) => id ? `Pago #${id}` : 'Pago';

const sumMethods = (methods) => normalizeList(methods).reduce((sum, method) => sum + toNumber(method?.amount), 0);

const sumAttachments = (methods) => normalizeList(methods).reduce((sum, method) => sum + toNumber(method?.attachmentsCount), 0);

const sumAllocations = (allocations) => normalizeList(allocations).reduce((sum, allocation) => sum + toNumber(allocation?.amount || allocation?.amount_applied), 0);

const getMethodIdentityKey = (method) => {
  if (method?.id) return `id:${String(method.id)}`;
  return [
    method?.type,
    toNumber(method?.amount).toFixed(2),
    method?.reference,
    method?.bank?.label,
    method?.date,
  ].map((value) => String(value || '').trim()).join('|');
};

const uniqueBy = (items, getKey) => {
  const seen = new Set();
  const out = [];

  normalizeList(items).forEach((item) => {
    const key = getKey(item);
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(item);
  });

  return out;
};

const uniqueText = (items) => uniqueBy(
  normalizeList(items).flatMap((value) => {
    if (Array.isArray(value)) return value;
    if (!value) return [];
    return [String(value).trim()];
  }).filter(Boolean),
  (value) => value
);

const normalizeList = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const firstValue = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');

const firstNumber = (...values) => {
  const found = values.find((value) => value !== undefined && value !== null && value !== '');
  return toNumber(found);
};

const parseTime = (value) => {
  if (!value) return 0;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const capitalize = (value) => {
  const text = String(value || '').trim();
  if (!text) return text;
  return `${text.charAt(0).toUpperCase()}${text.slice(1).toLowerCase()}`;
};
